/**
 * Onboarding state machine & step navigation.
 *
 * Listens on [data-onboarding] root element. Controls visibility of
 * .s-onboarding__card[data-step="..."] children and their internal widgets.
 *
 * State flow (see docs/superpowers/specs/2026-04-15-post-login-onboarding-flow-design.md):
 *   intro-odkrywca -> nick -> intro-walizka -> quiz-prompt -> (quiz | team-manual)
 *     -> team-result (-> team-manual or final submit)
 */

import { openAvatarModal, setSelectedAvatar, getAvatarPreviewUrl, resetAvatarSelection, commitAvatar } from './avatar-cropper';

const state = {
    nickname: '',
    chosenTeam: null,
    suggestedTeam: null,
    quizAnswers: [], // array of answer IDs in click order
    currentQuizIndex: 0,
    quizData: [],
    teamsData: {},
    urls: {},
};

let rootEl = null;

// ── DOM helpers ─────────────────────────────────────────────────────────────

function step(name) {
    return rootEl.querySelector(`.s-onboarding__card[data-step="${name}"]`);
}

function showStep(name) {
    rootEl.querySelectorAll('.s-onboarding__card').forEach(el => el.classList.remove('is-active'));
    const el = step(name);
    if (el) el.classList.add('is-active');

    if (name === 'quiz') {
        state.quizAnswers = [];
        state.currentQuizIndex = 0;
        renderQuizQuestion(state.currentQuizIndex);
    }
    if (name === 'team-result') renderResult();
}

// ── Nickname soft-check (debounced) ─────────────────────────────────────────

let nickDebounce = null;
const input = () => rootEl.querySelector('[data-nickname-input]');
const nickErr = () => rootEl.querySelector('[data-nickname-error]');

function validateNicknameLocal(value) {
    if (!value) return 'Podaj nick.';
    if (value.length < 3) return 'Nick musi mieć min. 3 znaki.';
    if (value.length > 30) return 'Nick może mieć max. 30 znaków.';
    if (!/^[A-Za-z0-9_-]+$/.test(value)) return 'Dozwolone znaki: litery, cyfry, _ i -.';
    return null;
}

function setNickError(msg) {
    const err = nickErr();
    if (!err) return;
    if (msg) {
        err.textContent = msg;
        err.hidden = false;
    } else {
        err.textContent = '';
        err.hidden = true;
    }
}

function onNicknameInput() {
    const value = (input()?.value || '').trim();

    // While the user is still typing we do NOT show local errors ("too short",
    // bad chars etc.) — those only appear on blur or submit. Any previously
    // displayed error clears as the user types.
    setNickError('');
    clearTimeout(nickDebounce);

    const localErr = validateNicknameLocal(value);
    if (localErr) return;

    // Local validation passed — schedule the uniqueness check.
    nickDebounce = setTimeout(async () => {
        try {
            const res = await fetch(`${state.urls.checkNickname}?value=${encodeURIComponent(value)}`, {
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
            });
            const data = await res.json();
            if (!data.available) {
                setNickError('Ten nick jest już zajęty.');
            }
        } catch {
            // network error — silent, final POST re-validates
        }
    }, 300);
}

function onNicknameBlur() {
    const value = (input()?.value || '').trim();
    if (!value) return; // empty field on blur → stay silent, user just hasn't filled yet
    const localErr = validateNicknameLocal(value);
    if (localErr) setNickError(localErr);
}

async function submitNick() {
    const value = (input()?.value || '').trim();
    const localErr = validateNicknameLocal(value);
    if (localErr) {
        setNickError(localErr);
        return;
    }

    const btn = rootEl.querySelector('[data-step="nick"] [data-action="submit-nick"]');
    if (btn) btn.disabled = true;

    try {
        const res = await fetch(`${state.urls.checkNickname}?value=${encodeURIComponent(value)}`, {
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        const data = await res.json();
        if (!data.available) {
            setNickError('Ten nick jest już zajęty.');
            return;
        }
    } catch {
        // network error — allow, final POST will catch it
    } finally {
        if (btn) btn.disabled = false;
    }

    state.nickname = value;
    showStep('quiz-prompt');
}

// ── Quiz ────────────────────────────────────────────────────────────────────

function renderQuizQuestion(index) {
    const q = state.quizData[index];
    if (!q) {
        finishQuiz();
        return;
    }

    const counterEl = rootEl.querySelector('[data-quiz-counter]');
    const qEl = rootEl.querySelector('[data-quiz-question]');
    const gridEl = rootEl.querySelector('[data-quiz-grid]');
    const descEl = rootEl.querySelector('[data-quiz-desc]');
    const nextBtn = rootEl.querySelector('[data-action="quiz-next"]');
    const backBtn = rootEl.querySelector('[data-action="quiz-back"]');

    // Przycisk "wstecz" pojawia się dopiero od pytania 2.
    if (backBtn) backBtn.hidden = index === 0;
    if (counterEl) counterEl.textContent = `PYTANIE ${index + 1}/${state.quizData.length}`;
    qEl.textContent = q.text || '';
    // Każda odpowiedź (drużyna) ma swój opis; przed wyborem pokazujemy ogólny opis pytania.
    const showAnswerDesc = (ans) => {
        if (!descEl) return;
        descEl.textContent = (ans && ans.description) ? ans.description : (q.description || '');
    };
    showAnswerDesc(null);
    gridEl.innerHTML = '';
    nextBtn.disabled = true;

    q.answers.forEach(ans => {
        const item = document.createElement('button');
        item.type = 'button';
        item.className = 's-onboarding__quiz-item';
        item.dataset.answerId = String(ans.id);
        item.dataset.team = ans.team || '';
        if (ans.image) {
            const img = document.createElement('img');
            img.src = ans.image;
            img.alt = '';
            item.appendChild(img);
        }
        gridEl.appendChild(item);
    });

    gridEl.querySelectorAll('.s-onboarding__quiz-item').forEach(btn => {
        btn.addEventListener('click', () => {
            gridEl.querySelectorAll('.s-onboarding__quiz-item').forEach(b => b.classList.remove('is-selected'));
            btn.classList.add('is-selected');
            nextBtn.disabled = false;
            showAnswerDesc(q.answers.find(a => String(a.id) === btn.dataset.answerId));
        });
    });

    // Wracając do pytania — przywróć wcześniej wybraną odpowiedź.
    const remembered = state.quizAnswers[index];
    if (remembered != null) {
        const selBtn = gridEl.querySelector(`.s-onboarding__quiz-item[data-answer-id="${remembered}"]`);
        if (selBtn) {
            selBtn.classList.add('is-selected');
            nextBtn.disabled = false;
            showAnswerDesc(q.answers.find(a => String(a.id) === String(remembered)));
        }
    }
}

function quizNext() {
    const grid = rootEl.querySelector('[data-quiz-grid]');
    const sel = grid.querySelector('.s-onboarding__quiz-item.is-selected');
    if (!sel) return;

    state.quizAnswers[state.currentQuizIndex] = parseInt(sel.dataset.answerId, 10);
    state.currentQuizIndex += 1;

    if (state.currentQuizIndex < state.quizData.length) {
        renderQuizQuestion(state.currentQuizIndex);
    } else {
        finishQuiz();
    }
}

function quizBack() {
    if (state.currentQuizIndex === 0) return;
    state.currentQuizIndex -= 1;
    // Odpowiedź zostaje w state.quizAnswers[index] → renderQuizQuestion ją przywróci.
    renderQuizQuestion(state.currentQuizIndex);
}

async function finishQuiz() {
    try {
        const res = await fetch(state.urls.quizResult, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ answers: state.quizAnswers }),
        });
        const data = await res.json();
        state.suggestedTeam = data.team || null;
    } catch {
        state.suggestedTeam = null;
    }

    if (state.suggestedTeam) {
        state.chosenTeam = state.suggestedTeam;
        showStep('team-result');
    } else {
        showStep('team-manual');
    }
}

// ── Result step ─────────────────────────────────────────────────────────────

function renderResult() {
    const team = state.chosenTeam;
    const teamInfo = team ? state.teamsData[team] : null;
    rootEl.querySelector('[data-team-name]').textContent = teamInfo?.label || '';

    // Team copy carries design line breaks as \n (kept out of the raw enum text
    // used elsewhere). Escape, then turn \n into <br> for the result layout.
    const setDesc = (sel, val) => {
        const el = rootEl.querySelector(sel);
        if (!el) return;
        el.innerHTML = (val || '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/\n/g, '<br>');
    };
    setDesc('[data-team-mindset]', teamInfo?.mindset);
    setDesc('[data-team-motto]', teamInfo?.motto);
    setDesc('[data-team-travel]', teamInfo?.travelStyle);
    setDesc('[data-team-beauty]', teamInfo?.beauty);

    // The emblem slot shows EITHER the uploaded avatar (if already picked) OR
    // the team icon. The avatar replaces the team art in-place, not in addition.
    const emblem = rootEl.querySelector('[data-team-emblem]');
    const avatarUrl = getAvatarPreviewUrl();
    if (avatarUrl) {
        emblem.src = avatarUrl;
        emblem.alt = 'Twoje zdjęcie profilowe';
        emblem.classList.add('is-avatar');
    } else if (teamInfo) {
        emblem.src = teamInfo.iconUrl;
        emblem.alt = teamInfo.label;
        emblem.classList.remove('is-avatar');
    }

    // Clear any stale final-error.
    const finalErr = rootEl.querySelector('[data-final-error]');
    finalErr.hidden = true;
    finalErr.textContent = '';
}

// ── Avatar picker (3 predefiniowane presety drużyny) ────────────────────────

function renderAvatarPresets() {
    const container = rootEl.querySelector('[data-avatar-presets]');
    const uploadBtn = container?.querySelector('.s-onboarding-avatar-modal__choice--upload');
    if (!container || !uploadBtn) return;

    container.querySelectorAll('[data-avatar-preset]').forEach(b => b.remove());

    const team = state.chosenTeam;
    const avatars = (team && state.teamsData[team] && state.teamsData[team].avatars) || [];
    avatars.forEach(av => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 's-onboarding-avatar-modal__choice';
        btn.dataset.action = 'avatar-pick-preset';
        btn.dataset.avatarPreset = av.key;
        btn.dataset.avatarUrl = av.url;
        const img = document.createElement('img');
        img.src = av.url;
        img.alt = '';
        btn.appendChild(img);
        container.insertBefore(btn, uploadBtn); // presety PRZED przyciskiem uploadu
    });

    const confirm = rootEl.querySelector('[data-action="avatar-confirm-preset"]');
    if (confirm) confirm.disabled = true;
}

function selectAvatarPreset(btn) {
    const container = rootEl.querySelector('[data-avatar-presets]');
    if (!container) return;
    container.querySelectorAll('.s-onboarding-avatar-modal__choice').forEach(b => b.classList.remove('is-selected'));
    btn.classList.add('is-selected');
    const confirm = rootEl.querySelector('[data-action="avatar-confirm-preset"]');
    if (confirm) confirm.disabled = false;
}

function confirmAvatarPreset() {
    const container = rootEl.querySelector('[data-avatar-presets]');
    const sel = container?.querySelector('.s-onboarding-avatar-modal__choice.is-selected[data-avatar-preset]');
    if (!sel) return;
    setSelectedAvatar(sel.dataset.avatarPreset, sel.dataset.avatarUrl);
}

// ── Final submit ────────────────────────────────────────────────────────────

async function finalize() {
    const finalErr = rootEl.querySelector('[data-final-error]');

    if (!state.chosenTeam) {
        finalErr.textContent = 'Wybierz drużynę.';
        finalErr.hidden = false;
        return;
    }

    // Upload avatara na S3 dopiero TERAZ (na submit) — nie przy kadrze.
    const avatarFilename = await commitAvatar();
    if (avatarFilename === null) {
        finalErr.textContent = 'Nie udało się wysłać zdjęcia. Spróbuj ponownie.';
        finalErr.hidden = false;
        return;
    }

    try {
        const res = await fetch(state.urls.submit, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({
                nickname: state.nickname,
                team: state.chosenTeam,
                avatarFilename: avatarFilename || '',
            }),
        });

        if (res.ok) {
            const data = await res.json();
            window.location.href = data.redirect || '/';
            return;
        }

        if (res.status === 422) {
            const data = await res.json();
            const firstError = Object.values(data.errors || {})[0]?.[0] || 'Nie udało się zapisać danych.';
            finalErr.textContent = firstError;
            finalErr.hidden = false;

            if (data.errors?.nickname) {
                // Send user back to step 2 with the failing nick to fix.
                setNickError(data.errors.nickname[0]);
                showStep('nick');
            }
            return;
        }

        finalErr.textContent = 'Błąd serwera. Spróbuj ponownie.';
        finalErr.hidden = false;
    } catch {
        finalErr.textContent = 'Błąd sieci. Spróbuj ponownie.';
        finalErr.hidden = false;
    }
}

// ── Team pick (step 7) ──────────────────────────────────────────────────────

function pickTeam(teamValue) {
    if (!state.teamsData[teamValue]) return;
    // Zmiana na INNĄ drużynę czyści wcześniej wybrany awatar — wynik znów pokaże
    // domyślną ikonę nowej drużyny zamiast presetu z poprzedniej.
    if (teamValue !== state.chosenTeam) resetAvatarSelection();
    state.chosenTeam = teamValue;
    showStep('team-result');
}

// ── Bootstrap ───────────────────────────────────────────────────────────────

function init() {
    rootEl = document.querySelector('[data-onboarding]');
    if (!rootEl) return;

    try {
        state.teamsData = JSON.parse(rootEl.dataset.teams || '{}');
        state.quizData = JSON.parse(rootEl.dataset.quiz || '[]');
    } catch {
        state.teamsData = {};
        state.quizData = [];
    }
    state.urls = {
        checkNickname: rootEl.dataset.checkNicknameUrl,
        quizResult:    rootEl.dataset.quizResultUrl,
        avatarUpload:  rootEl.dataset.avatarUploadUrl,
        submit:        rootEl.dataset.submitUrl,
    };
    state.nickname = rootEl.dataset.currentNickname || '';

    // If no quiz questions exist, hide the TAK button on quiz-prompt.
    if (state.quizData.length === 0) {
        const yes = rootEl.querySelector('[data-quiz-yes]');
        if (yes) yes.hidden = true;
    }

    // Nickname input handler — live soft-check on input, local validation on blur/submit only
    const nick = input();
    if (nick) {
        nick.addEventListener('input', onNicknameInput);
        nick.addEventListener('blur', onNicknameBlur);
    }

    // Global action delegation
    rootEl.addEventListener('click', (e) => {
        const actionEl = e.target.closest('[data-action]');
        if (!actionEl) return;
        const action = actionEl.dataset.action;

        switch (action) {
            case 'goto':
                showStep(actionEl.dataset.target);
                break;
            case 'submit-nick':
                submitNick();
                break;
            case 'quiz-next':
                quizNext();
                break;
            case 'quiz-back':
                quizBack();
                break;
            case 'pick-team':
                pickTeam(actionEl.dataset.team);
                break;
            case 'open-avatar-modal':
                renderAvatarPresets();
                openAvatarModal(rootEl, state.urls.avatarUpload, () => renderResult());
                break;
            case 'avatar-pick-preset':
                selectAvatarPreset(actionEl);
                break;
            case 'avatar-confirm-preset':
                confirmAvatarPreset();
                break;
            case 'close-avatar-modal':
                // Handled inside avatar-cropper module.
                break;
            case 'finalize':
                finalize();
                break;
        }
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}

export {};
